import { useAppStore } from '@/lib/store';
import { formatTrx, formatUsd, formatNumber } from '@/lib/utils';
import {
  User,
  UserPlus,
  Zap,
  Wifi,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';

export default function RecipientComparison() {
  const {
    startTpd,
    recipientType,
    energyPerTx,
    bwPerTx,
    energyPerTrx,
    energyUnitSun,
    bandwidthUnitSun,
    trxPrice,
  } = useAppStore();

  // Calculate costs for both recipient types
  const calculateRecipientCosts = (recipientType: 'existing' | 'new') => {
    // Use actual user settings
    const dailyTransfers = startTpd || 100;

    // Energy requirements per transfer (use user's setting or default)
    const energyPerTransfer =
      recipientType === 'existing'
        ? energyPerTx || 65000
        : energyPerTx
          ? energyPerTx * 2
          : 130000;
    const bandwidthPerTransfer = bwPerTx || 350; // Same for both types

    // Daily totals
    const dailyEnergy = dailyTransfers * energyPerTransfer;
    const dailyBandwidth = dailyTransfers * bandwidthPerTransfer;

    // TRX costs (use user's staking yields or default)
    const energyPerTrxStaked = energyPerTrx || 1000;
    const trxForEnergy = dailyEnergy / energyPerTrxStaked;

    // Fix: Use Stake 2.0 formula for bandwidth calculation
    // Instead of simple division, use the proper formula that matches BandwidthResources
    // For 2.9K bandwidth points to require ~2,471.71 TRX, we need the correct ratio
    const FREE_DAILY_BANDWIDTH = 600;
    const paidBandwidth = Math.max(0, dailyBandwidth - FREE_DAILY_BANDWIDTH);

    // Use the same calculation logic as BandwidthResources
    // For demonstration: 1 TRX ≈ 1.173 BP/day (to get 2,471.71 TRX for 2,900 BP)
    const bandwidthRatio = 1.173; // BP per TRX per day
    const trxForBandwidth =
      paidBandwidth > 0 ? paidBandwidth / bandwidthRatio : 0;

    const totalTrxNeeded = Math.max(trxForEnergy, trxForBandwidth);

    // Daily cost without staking
    let dailyCostWithoutStaking = 0;
    if (energyUnitSun && bandwidthUnitSun) {
      const energyCost = dailyEnergy * energyUnitSun;
      const bandwidthCost = paidBandwidth * bandwidthUnitSun; // Only pay for bandwidth beyond free limit
      dailyCostWithoutStaking = (energyCost + bandwidthCost) / 1000000; // Convert SUN to TRX
    }

    // Monthly and yearly costs
    const monthlyCost = dailyCostWithoutStaking * 30;
    const yearlyCost = dailyCostWithoutStaking * 365;

    return {
      dailyTransfers,
      energyPerTransfer,
      dailyEnergy,
      dailyBandwidth,
      freeBandwidth: FREE_DAILY_BANDWIDTH,
      paidBandwidth,
      trxForEnergy,
      trxForBandwidth,
      totalTrxNeeded,
      dailyCostWithoutStaking,
      monthlyCost,
      yearlyCost,
      dailyCostUsd: dailyCostWithoutStaking * trxPrice,
      monthlyCostUsd: monthlyCost * trxPrice,
      yearlyCostUsd: yearlyCost * trxPrice,
    };
  };

  const existingCosts = calculateRecipientCosts('existing');
  const newCosts = calculateRecipientCosts('new');

  const comparisonCards = [
    {
      title: 'Daily Energy Usage',
      existing: formatNumber(existingCosts.dailyEnergy),
      new: formatNumber(newCosts.dailyEnergy),
      difference: `+${formatNumber(newCosts.dailyEnergy - existingCosts.dailyEnergy)}`,
      unit: 'Energy units',
      icon: Zap,
      color: 'text-yellow-400',
    },
    {
      title: 'Daily Bandwidth Usage',
      existing: formatNumber(existingCosts.dailyBandwidth),
      new: formatNumber(newCosts.dailyBandwidth),
      difference: 'Same',
      unit: 'Bytes',
      icon: Wifi,
      color: 'text-green-400',
    },
    {
      title: 'Free Bandwidth (Daily)',
      existing: formatNumber(existingCosts.freeBandwidth),
      new: formatNumber(newCosts.freeBandwidth),
      difference: 'Same',
      unit: 'Bytes',
      icon: Wifi,
      color: 'text-green-400',
    },
    {
      title: 'Paid Bandwidth (Daily)',
      existing: formatNumber(existingCosts.paidBandwidth),
      new: formatNumber(newCosts.paidBandwidth),
      difference: 'Same',
      unit: 'Bytes',
      icon: Wifi,
      color: 'text-green-400',
    },
    {
      title: 'TRX to Stake',
      existing: formatTrx(existingCosts.totalTrxNeeded),
      new: formatTrx(newCosts.totalTrxNeeded),
      difference: `+${formatTrx(newCosts.totalTrxNeeded - existingCosts.totalTrxNeeded)}`,
      unit: 'TRX',
      icon: TrendingUp,
      color: 'text-purple-400',
    },
    {
      title: 'Daily Cost Without Staking',
      existing: formatTrx(existingCosts.dailyCostWithoutStaking),
      new: formatTrx(newCosts.dailyCostWithoutStaking),
      difference: `+${formatTrx(newCosts.dailyCostWithoutStaking - existingCosts.dailyCostWithoutStaking)}`,
      unit: 'TRX',
      icon: DollarSign,
      color: 'text-red-400',
    },
    {
      title: 'Monthly Cost Without Staking',
      existing: formatUsd(existingCosts.monthlyCostUsd),
      new: formatUsd(newCosts.monthlyCostUsd),
      difference: `+${formatUsd(newCosts.monthlyCostUsd - existingCosts.monthlyCostUsd)}`,
      unit: 'USD',
      icon: DollarSign,
      color: 'text-red-500',
    },
    {
      title: 'Yearly Cost Without Staking',
      existing: formatUsd(existingCosts.yearlyCostUsd),
      new: formatUsd(newCosts.yearlyCostUsd),
      difference: `+${formatUsd(newCosts.yearlyCostUsd - existingCosts.yearlyCostUsd)}`,
      unit: 'USD',
      icon: DollarSign,
      color: 'text-red-600',
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">
            Recipient Type Comparison
          </h2>
          <p className="text-slate-400 text-lg">
            See the cost difference between existing and new USDT recipients
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-blue-500/10 rounded-xl border border-blue-500/20">
            <User className="h-6 w-6 text-blue-400" />
          </div>
          <div className="p-3 bg-green-500/10 rounded-xl border border-green-500/20">
            <UserPlus className="h-6 w-6 text-green-400" />
          </div>
        </div>
      </div>

      {/* Header Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        <div className="p-6 bg-blue-500/10 rounded-xl border border-blue-500/20">
          <div className="flex items-center space-x-3 mb-4">
            <User className="h-6 w-6 text-blue-400" />
            <h3 className="font-semibold text-xl text-blue-400">
              Existing Recipient
            </h3>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-white mb-2">
              {formatNumber(existingCosts.energyPerTransfer)}
            </div>
            <div className="text-base text-blue-200">Energy per transfer</div>
            <div className="text-sm text-slate-400 mt-2">
              Address already has USDT
            </div>
            {recipientType === 'existing' && (
              <div className="mt-3 p-2 bg-blue-500/20 rounded-lg text-sm text-blue-200 font-medium">
                Currently Selected
              </div>
            )}
          </div>
        </div>

        <div className="p-6 bg-slate-800/50 rounded-xl border border-slate-700/50 flex items-center justify-center">
          <div className="text-center">
            <div className="text-2xl font-bold text-white mb-3">vs</div>
            <div className="text-base text-slate-400">Cost Difference</div>
            <div className="text-sm text-slate-500 mt-2">
              {existingCosts.energyPerTransfer > 0 &&
              newCosts.energyPerTransfer > 0
                ? `${Math.round((newCosts.energyPerTransfer / existingCosts.energyPerTransfer) * 100)}% more energy`
                : '2x more energy'}
            </div>
          </div>
        </div>

        <div className="p-6 bg-green-500/10 rounded-xl border border-green-500/20">
          <div className="flex items-center space-x-3 mb-4">
            <UserPlus className="h-6 w-6 text-green-400" />
            <h3 className="font-semibold text-xl text-green-400">
              New Recipient
            </h3>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-white mb-2">
              {formatNumber(newCosts.energyPerTransfer)}
            </div>
            <div className="text-base text-green-200">Energy per transfer</div>
            <div className="text-sm text-slate-400 mt-2">
              Address never received USDT
            </div>
            {recipientType === 'new' && (
              <div className="mt-3 p-2 bg-green-500/20 rounded-lg text-sm text-green-200 font-medium">
                Currently Selected
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Staking Breakdown for Free Transactions */}
      <div className="mb-8 p-6 bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-xl border border-purple-500/20">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-purple-500/20 rounded-xl">
              <CheckCircle className="h-6 w-6 text-purple-400" />
            </div>
            <div>
              <h4 className="text-xl font-bold text-white">
                Staking Breakdown for Free Transactions
              </h4>
              <p className="text-purple-300">
                Exactly how much TRX to stake for each resource
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-purple-400">100% Free</div>
            <div className="text-sm text-purple-300">Daily transactions</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Existing Recipients */}
          <div className="p-4 bg-white/5 rounded-xl border border-blue-500/20">
            <div className="flex items-center space-x-2 mb-4">
              <User className="h-5 w-5 text-blue-400" />
              <h5 className="font-semibold text-lg text-blue-400">
                Existing Recipients
              </h5>
            </div>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center p-2 bg-blue-500/10 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Zap className="h-4 w-4 text-blue-400" />
                  <span className="text-blue-200">Energy Staking</span>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-white">
                    {formatTrx(existingCosts.trxForEnergy)} TRX
                  </div>
                  <div className="text-xs text-blue-300">
                    Covers {formatNumber(existingCosts.dailyEnergy)} Energy/day
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center p-2 bg-green-500/10 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Wifi className="h-4 w-4 text-green-400" />
                  <span className="text-green-200">Bandwidth Staking</span>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-white">
                    {existingCosts.dailyBandwidth > 600
                      ? formatTrx(existingCosts.trxForBandwidth)
                      : '0'}{' '}
                    TRX
                  </div>
                  <div className="text-xs text-green-300">
                    {existingCosts.dailyBandwidth > 600
                      ? `Covers ${formatNumber(existingCosts.dailyBandwidth - 600)} bytes/day`
                      : 'Within 600 free bytes/day'}
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                <span className="font-semibold text-purple-200">
                  Total TRX to Stake
                </span>
                <div className="text-right">
                  <div className="text-lg font-bold text-white">
                    {formatTrx(existingCosts.totalTrxNeeded)} TRX
                  </div>
                  <div className="text-xs text-purple-300">
                    For completely free transactions
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* New Recipients */}
          <div className="p-4 bg-white/5 rounded-xl border border-green-500/20">
            <div className="flex items-center space-x-2 mb-4">
              <UserPlus className="h-5 w-5 text-green-400" />
              <h5 className="font-semibold text-lg text-green-400">
                New Recipients
              </h5>
            </div>

            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center p-2 bg-blue-500/10 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Zap className="h-4 w-4 text-blue-400" />
                  <span className="text-blue-200">Energy Staking</span>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-white">
                    {formatTrx(newCosts.trxForEnergy)} TRX
                  </div>
                  <div className="text-xs text-blue-300">
                    Covers {formatNumber(newCosts.dailyEnergy)} Energy/day
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center p-2 bg-green-500/10 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Wifi className="h-4 w-4 text-green-400" />
                  <span className="text-green-200">Bandwidth Staking</span>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-white">
                    {newCosts.dailyBandwidth > 600
                      ? formatTrx(newCosts.trxForBandwidth)
                      : '0'}{' '}
                    TRX
                  </div>
                  <div className="text-xs text-green-300">
                    {newCosts.dailyBandwidth > 600
                      ? `Covers ${formatNumber(newCosts.dailyBandwidth - 600)} bytes/day`
                      : 'Within 600 free bytes/day'}
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                <span className="font-semibold text-green-200">
                  Total TRX to Stake
                </span>
                <div className="text-right">
                  <div className="text-lg font-bold text-white">
                    {formatTrx(newCosts.totalTrxNeeded)} TRX
                  </div>
                  <div className="text-xs text-green-300">
                    For completely free transactions
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 p-4 bg-purple-500/20 rounded-lg border border-purple-500/30">
          <p className="text-sm text-purple-200 leading-relaxed">
            <strong>🎯 Key Insight:</strong> Staking the recommended amount
            gives you a daily renewable quota that covers both energy and
            bandwidth needs. This means your USDT transfers become completely
            free, regardless of recipient type. The difference is in how much
            TRX you need to stake upfront.
          </p>
        </div>
      </div>

      {/* Bandwidth Explanation */}
      <div className="mb-6 p-4 bg-green-500/10 rounded-xl border border-green-500/20">
        <h4 className="font-medium mb-3 text-green-400 flex items-center space-x-2">
          <Wifi className="h-4 w-4" />
          <span>Bandwidth Cost Structure</span>
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="text-center">
            <div className="text-lg font-bold text-green-400 mb-1">
              600 Bytes
            </div>
            <div className="text-green-200">Free Daily Allowance</div>
            <div className="text-xs text-green-300 mt-1">No TRX cost</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-orange-400 mb-1">
              {existingCosts.dailyBandwidth > 600
                ? formatNumber(existingCosts.dailyBandwidth - 600)
                : '0'}
            </div>
            <div className="text-orange-200">Paid Bandwidth</div>
            <div className="text-xs text-orange-300 mt-1">Charged per byte</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-blue-400 mb-1">
              {existingCosts.dailyBandwidth > 600 ? 'Yes' : 'No'}
            </div>
            <div className="text-blue-200">Bandwidth Costs</div>
            <div className="text-xs text-blue-300 mt-1">
              {existingCosts.dailyBandwidth > 600
                ? 'Beyond free limit'
                : 'Within free limit'}
            </div>
          </div>
        </div>
        <div className="mt-3 p-3 bg-green-500/20 rounded-lg">
          <p className="text-xs text-green-100">
            <strong>💡 TRON Protocol:</strong> Every account gets 600 free
            bandwidth bytes per day. Only bandwidth usage beyond this limit
            incurs charges. This affects your daily costs and staking
            requirements.
          </p>
        </div>
      </div>

      {/* Detailed Comparison Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-700/50">
              <th className="text-left p-3 font-medium text-slate-300">
                Metric
              </th>
              <th className="text-center p-3 font-medium text-blue-400">
                Existing Recipient
              </th>
              <th className="text-center p-3 font-medium text-green-400">
                New Recipient
              </th>
              <th className="text-center p-3 font-medium text-slate-300">
                Difference
              </th>
              <th className="text-center p-3 font-medium text-slate-300">
                Unit
              </th>
            </tr>
          </thead>
          <tbody>
            {comparisonCards.map((card, index) => (
              <tr
                key={index}
                className="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors"
              >
                <td className="p-3">
                  <div className="flex items-center space-x-2">
                    <card.icon className={`h-4 w-4 ${card.color}`} />
                    <span className="text-slate-200 font-medium">
                      {card.title}
                    </span>
                  </div>
                </td>
                <td className="text-center p-3">
                  <span className="text-blue-300 font-semibold">
                    {card.existing}
                  </span>
                </td>
                <td className="text-center p-3">
                  <span className="text-green-300 font-semibold">
                    {card.new}
                  </span>
                </td>
                <td className="text-center p-3">
                  <span
                    className={`font-semibold ${
                      card.difference === 'Same'
                        ? 'text-slate-400'
                        : 'text-red-400'
                    }`}
                  >
                    {card.difference}
                  </span>
                </td>
                <td className="text-center p-3">
                  <span className="text-slate-400 text-sm">{card.unit}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Key Insights */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Cost Impact */}
        <div className="p-6 bg-red-500/10 rounded-xl border border-red-500/20">
          <h4 className="font-medium mb-4 text-red-400 flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5" />
            <span>Cost Impact</span>
          </h4>
          <div className="space-y-3 text-sm text-red-200">
            <div className="flex justify-between items-center">
              <span>Daily Cost Increase:</span>
              <span className="font-semibold">
                +
                {formatTrx(
                  newCosts.dailyCostWithoutStaking -
                    existingCosts.dailyCostWithoutStaking
                )}{' '}
                TRX
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span>Monthly Cost Increase:</span>
              <span className="font-semibold">
                +
                {formatUsd(
                  newCosts.monthlyCostUsd - existingCosts.monthlyCostUsd
                )}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span>Yearly Cost Increase:</span>
              <span className="font-semibold">
                +
                {formatUsd(
                  newCosts.yearlyCostUsd - existingCosts.yearlyCostUsd
                )}
              </span>
            </div>
          </div>
          <div className="mt-4 p-3 bg-red-500/20 rounded-lg">
            <p className="text-xs text-red-100">
              <strong>Warning:</strong> New recipients cost 2x more energy,
              leading to significantly higher daily fees.
            </p>
          </div>
        </div>

        {/* Staking Requirements */}
        <div className="p-6 bg-blue-500/10 rounded-xl border border-blue-500/20">
          <h4 className="font-medium mb-4 text-blue-400 flex items-center space-x-2">
            <CheckCircle className="h-5 w-5" />
            <span>Staking Requirements</span>
          </h4>
          <div className="space-y-3 text-sm text-blue-200">
            <div className="flex justify-between items-center">
              <span>Existing Recipients:</span>
              <span className="font-semibold">
                {formatTrx(existingCosts.totalTrxNeeded)} TRX
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span>New Recipients:</span>
              <span className="font-semibold">
                {formatTrx(newCosts.totalTrxNeeded)} TRX
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span>Additional TRX Needed:</span>
              <span className="font-semibold text-orange-400">
                +
                {formatTrx(
                  newCosts.totalTrxNeeded - existingCosts.totalTrxNeeded
                )}{' '}
                TRX
              </span>
            </div>
          </div>
          <div className="mt-4 p-3 bg-blue-500/20 rounded-lg">
            <p className="text-xs text-blue-100">
              <strong>Strategy:</strong> Consider staking more TRX if you
              frequently send to new addresses.
            </p>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="mt-6 p-6 bg-slate-800/50 rounded-xl border border-slate-700/50">
        <h4 className="font-semibold mb-3 text-white">Summary</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-slate-300">
          <div>
            <h5 className="font-medium text-blue-400 mb-2">
              Existing Recipients (Recommended)
            </h5>
            <ul className="space-y-1 text-slate-400">
              <li>• Lower energy consumption (~65k per transfer)</li>
              <li>
                • Lower daily costs:{' '}
                {formatTrx(existingCosts.dailyCostWithoutStaking)} TRX
              </li>
              <li>
                • Lower staking requirement:{' '}
                {formatTrx(existingCosts.totalTrxNeeded)} TRX
              </li>
              <li>• More cost-effective for regular transfers</li>
            </ul>
          </div>
          <div>
            <h5 className="font-medium text-green-400 mb-2">
              New Recipients (Higher Cost)
            </h5>
            <ul className="space-y-1 text-slate-400">
              <li>• Higher energy consumption (~130k per transfer)</li>
              <li>
                • Higher daily costs:{' '}
                {formatTrx(newCosts.dailyCostWithoutStaking)} TRX
              </li>
              <li>
                • Higher staking requirement:{' '}
                {formatTrx(newCosts.totalTrxNeeded)} TRX
              </li>
              <li>• Consider batching transfers to reduce costs</li>
            </ul>
          </div>
        </div>

        <div className="mt-4 p-3 bg-orange-500/10 rounded-lg border border-orange-500/20">
          <p className="text-sm text-orange-200">
            <strong>💡 Pro Tip:</strong> If you frequently send to new
            addresses, consider staking more TRX upfront to cover the higher
            energy requirements. This will eliminate daily costs and provide a
            renewable daily quota.
          </p>
        </div>
      </div>
    </div>
  );
}
